import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import alertKnockoutTokens from '@amzn/meridian-tokens/base/icon/alert-knockout';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { BOX_SYNC_TARGET_REFRESH_SCHEDULE, BOX_SYSTEM_ACCOUNT_IDENTITY } from 'src/constants';
import { useUser } from 'src/contexts/UserContext';
import { useDebounce } from 'src/hooks/useDebounce';
import { useCreateSyncTargetMutation, useGetBoxFolderQuery } from 'src/store/bender-api';
import { SyncTargetTypes } from 'src/types/sync-target.types';
import { parseBoxFolderInput } from 'src/utils/syncTarget';
import { normalizeBenderError } from 'src/utils/benderErrors';
import { Logger } from 'src/utils/logger';

const PROBE_DEBOUNCE_MS = 400;

/** Fixed so the folder id, the confirmation, and the warning all share one footprint. */
const MODAL_WIDTH = 480;

/**
 * Reserved height for the probe-result region, sized to the tallest state: the warning
 * alert holding two lines of copy plus its address line. Holding the space means a result
 * appearing or changing never resizes the modal mid-interaction.
 *
 * Re-measure whenever that alert gains or loses a row — this was 108 while Re-check still
 * lived inside the alert, and stayed 108 after the button moved to the footer, leaving
 * ~36px of dead space visible below the warning.
 */
const STATUS_MIN_HEIGHT = 72;

type ProbeState = 'empty' | 'unparseable' | 'debouncing' | 'checking' | 'ok' | 'inaccessible' | 'unavailable';

/** Only `ok` permits enrollment; every other state has a reason to show. */
const PROBE_MESSAGE_KEYS: Partial<Record<ProbeState, string>> = {
  unparseable: 'box_folder_unparseable',
  inaccessible: 'box_folder_inaccessible_short',
  unavailable: 'box_folder_unavailable',
};

interface AddBoxFolderModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Adds a Box folder to the sync registry.
 *
 * Access is verified before enrolling: Bender's enumerator would otherwise accept the
 * folder and only discover it cannot read it on the first scheduled run, leaving a
 * folder that looks enrolled but silently yields nothing.
 *
 * Uses plain local state rather than react-hook-form — there is a single field, and
 * the Add button's enabled state is just `probeState === 'ok'`.
 */
export const AddBoxFolderModal: React.FC<AddBoxFolderModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const { user } = useUser();

  const [rawInput, setRawInput] = useState('');
  const [createSyncTarget, { isLoading: isAdding }] = useCreateSyncTargetMutation();

  const folderId = parseBoxFolderInput(rawInput);
  const [probeId] = useDebounce(folderId ?? '', PROBE_DEBOUNCE_MS) as [string, boolean];

  const { data, isFetching, error, refetch } = useGetBoxFolderQuery(
    { folderId: probeId },
    { skip: !probeId || !open },
  );

  /**
   * Derived, not latched. A `useRef` latch keyed on the `isFetching` transition would
   * never release when RTK Query serves a warm cache synchronously; comparing
   * `probeId !== folderId` behaves identically on cold and warm caches.
   */
  const resolveProbeState = (): ProbeState => {
    if (!rawInput.trim()) return 'empty';
    if (!folderId) return 'unparseable';
    // Still typing, or the debounce hasn't caught up.
    if (probeId !== folderId) return 'debouncing';
    if (isFetching) return 'checking';
    if (error) {
      const { status } = normalizeBenderError(error);
      // 404 carries both user-fixable causes and cannot be reported as "no such folder":
      // Box answers `GET /folders/:id` with 404 for a folder that exists but is not shared
      // with our system account, because it will not confirm the existence of something
      // you cannot see. So a 404 means "wrong id, OR not shared yet" — verified against
      // the real API, where both cases came back 404.
      if (status === 404) return 'inaccessible';
      // Everything else is a failure the user cannot act on, so it must not carry the
      // share-the-folder instruction. Bender answers 502 when its own Box credentials or
      // app authorization are rejected, which sharing a folder cannot fix; a 403 is not
      // expected from this endpoint at all and is handled here defensively. Access is
      // unconfirmed in every one of these cases, so adding stays blocked — enrolling
      // anyway yields a folder that silently produces nothing.
      return 'unavailable';
    }
    // Rejects a stale response for a folder id the user has since changed.
    if (data?.id === folderId) return 'ok';
    return 'debouncing';
  };

  const probeState = resolveProbeState();
  const canAdd = probeState === 'ok' && !isAdding;

  const close = () => {
    setRawInput('');
    onClose();
  };

  const handleAdd = async () => {
    if (!canAdd || !folderId) return;

    const userAlias = user?.username;
    if (!userAlias) {
      // Bender's userAlias is @IsNotEmpty(), so an empty one is a guaranteed 400.
      Logger.error('Cannot add a Box folder without a resolved user alias');
      createToast({ type: AlertType.ERROR, message: t('box_folder_error_generic') });
      return;
    }

    try {
      await createSyncTarget({
        syncTargetType: SyncTargetTypes.BOX,
        targetId: folderId,
        refreshSchedule: BOX_SYNC_TARGET_REFRESH_SCHEDULE,
        userAlias,
      }).unwrap();
      createToast({ type: AlertType.SUCCESS, message: t('box_folder_added', { name: data?.name ?? folderId }) });
      close();
    } catch (caught) {
      const normalized = normalizeBenderError(caught);
      Logger.error(`Failed to add Box folder ${folderId}`, {
        status: normalized.status,
        detail: normalized.message,
      });

      // 409 means a registry row for this folder already exists — which is the state the
      // user was asking for. Reported as information, not failure, and the modal closes.
      //
      // The table behind it does refetch: RTK Query matches mutation tags on
      // `isAnyOf(isFulfilled, isRejectedWithValue)`, and a baseQuery returning `{ error }`
      // rejects with value, so `invalidatesTags` fires on this path too. A test in
      // bender-api.test.ts pins that, since it is easy to assume otherwise.
      //
      // What the refetch cannot promise is that the row is *on screen*: the list is
      // paginated and ordered `createdAt desc`, so a folder someone enrolled earlier may
      // sit on a later page, and the endpoint has no filter to jump to it. Hence the toast
      // says the folder is already added rather than pointing at a row.
      if (normalized.status === 409) {
        createToast({ type: AlertType.SUCCESS, message: t('box_folder_already_added') });
        close();
        return;
      }

      createToast({
        type: AlertType.ERROR,
        message: t(
          normalized.status === 400
            ? 'box_folder_add_rejected'
            // 502 leaves nothing enrolled and cleans up any partial schedule, so
            // retrying is safe.
            : 'box_folder_add_retry_error',
        ),
      });
    }
  };

  /**
   * The address is far too long and too arbitrary to retype from a screen, so copying it
   * has to work. `navigator.clipboard` is absent outside a secure context and can reject
   * on a permissions policy, and both cases must report rather than silently no-op —
   * `await undefined` resolves, which would otherwise show a success toast for nothing.
   */
  const handleCopyIdentity = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(BOX_SYSTEM_ACCOUNT_IDENTITY);
      createToast({ type: AlertType.SUCCESS, message: t('box_folder_identity_copied') });
    } catch (caught) {
      Logger.error('Failed to copy the Box system account address', {
        detail: caught instanceof Error ? caught.message : String(caught),
      });
      // The address stays on screen and selectable, so the fallback is a real one.
      createToast({ type: AlertType.ERROR, message: t('box_folder_copy_failed') });
    }
  };

  const indicator = (() => {
    const content = (() => {
      if (probeState === 'checking') {
        return <Loader data-testid="add-box-folder-checking" size="small" />;
      }

      if (probeState === 'ok') {
        return (
          <Tooltip
            position="top"
            title={t('box_folder_verified', {
              name: data?.name ?? '',
              count: String(data?.itemCollection?.totalCount ?? 0),
            })}
          >
            {/* Tooltip needs a DOM node to anchor to; Icon does not forward a ref. */}
            <div data-testid="add-box-folder-verified" style={{ display: 'inline-flex' }}>
              <Icon color="success" tokens={checkCircleTokens} />
            </div>
          </Tooltip>
        );
      }

      const messageKey = PROBE_MESSAGE_KEYS[probeState];
      if (!messageKey) {
        return undefined;
      }

      return (
        <Tooltip position="top" title={t(messageKey)}>
          <div data-testid="add-box-folder-invalid" style={{ display: 'inline-flex' }}>
            <Icon color="error" tokens={alertKnockoutTokens} />
          </div>
        </Tooltip>
      );
    })();

    if (!content) {
      return undefined;
    }

    // Meridian wraps only a *string* suffix in its own endcap; a node is dropped straight
    // into the input box, which is `display: flex` with no `alignItems` — so a bare node
    // stretches the full height and paints at the top, flush to the border. This mirrors
    // what Meridian's own `suffixIconTokens` path does: center it, and inset it from the
    // right edge.
    return (
      <Row alignmentVertical="center" spacingInset="none 300 none none">
        {content}
      </Row>
    );
  })();

  const showStatusAlert = probeState === 'inaccessible' || probeState === 'unavailable';

  return (
    // Fixed width, and a status region with a reserved height: every probe result renders
    // into the same footprint, so the modal cannot resize under the pointer while the user
    // is still typing a folder id. MODAL_WIDTH / STATUS_MIN_HEIGHT are sized to the tallest
    // state (the warning alert with its address line).
    <Modal
      data-testid="add-box-folder-modal"
      open={open}
      title={t('box_folder_add')}
      width={MODAL_WIDTH}
      onClose={close}
    >
      <Column spacing="300">
        <Input
          data-testid="add-box-folder-input"
          label={t('box_folder_label')}
          placeholder={t('box_folder_placeholder')}
          suffix={indicator}
          value={rawInput}
          onChange={setRawInput}
        />
        <Text color="secondary" type="b200">{t('box_folder_help')}</Text>

        <Column data-testid="add-box-folder-status" minHeight={STATUS_MIN_HEIGHT}>
          {probeState === 'ok' && data && (
            <Text data-testid="add-box-folder-resolved" type="b300">
              {t('box_folder_verified', {
                name: data.name,
                count: String(data.itemCollection?.totalCount ?? 0),
              })}
            </Text>
          )}

          {showStatusAlert && (
            // An Alert rather than only the tooltip: a folder we cannot see is the one
            // failure the user can actually fix, so it needs to be impossible to miss.
            <Alert data-testid="add-box-folder-alert" size="small" type="warning">
              <Column spacing="200">
                <Text>
                  {probeState === 'inaccessible'
                    ? t('box_folder_inaccessible')
                    : t('box_folder_unavailable')}
                </Text>

                {probeState === 'inaccessible' && (
                  // On its own line rather than inside the sentence: the address is long
                  // enough to wrap mid-instruction, and it needs the copy button adjacent.
                  <Row alignmentVertical="center" spacing="100">
                    <Text data-testid="add-box-folder-identity" type="b200">
                      {BOX_SYSTEM_ACCOUNT_IDENTITY}
                    </Text>
                    <Button
                      aria-label={t('box_folder_copy_identity')}
                      data-testid="add-box-folder-copy-identity-button"
                      size="small"
                      type="icon"
                      onClick={() => { void handleCopyIdentity(); }}
                    >
                      <Icon tokens={copyTokens} />
                    </Button>
                  </Row>
                )}
              </Column>
            </Alert>
          )}
        </Column>
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200">
          {/*
            Re-check belongs here rather than inside the Alert: as a link-style button in
            flowing text its baseline never lined up, and after sharing the folder in Box
            re-probing is a footer-level action like the other two.
          */}
          {showStatusAlert && (
            <Button
              data-testid="add-box-folder-recheck-button"
              disabled={isAdding}
              type="secondary"
              onClick={() => { void refetch(); }}
            >
              {t('box_folder_recheck')}
            </Button>
          )}
          <Button data-testid="add-box-folder-cancel-button" disabled={isAdding} type="tertiary" onClick={close}>
            {t('form_global_cancel')}
          </Button>
          <Button
            data-testid="add-box-folder-submit-button"
            disabled={!canAdd}
            type="primary"
            onClick={() => { void handleAdd(); }}
          >
            {isAdding ? <Loader size="small" /> : t('box_folder_add_submit')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
